import React, { useState } from "react";
import Card from "components/card";
import { MdQuestionMark, MdSave, MdStar, MdStarBorder } from "react-icons/md";
import { AiFeedbackPayload, AiLearningExample } from "services/questionnaireService";

// Check if AI feedback is enabled
const IA_FEEDBACK_ENABLED = true;
// const IA_FEEDBACK_ENABLED = process.env.NEXT_PUBLIC_IA_FEEDBACK_ENABLED === "true";

interface AiInterpretationProps {
  aiEnhancedInterpretation: string;
  aiLearningExample?: AiLearningExample;
  detailId: string;
  onSubmitFeedback: (payload: AiFeedbackPayload) => Promise<void>;
}

const AiInterpretation: React.FC<AiInterpretationProps> = ({
  aiEnhancedInterpretation,
  aiLearningExample,
  detailId,
  onSubmitFeedback
}) => {
  // AI feedback states
  const [rating, setRating] = useState<number>(aiLearningExample?.quality_rating || 0);
  const [correctedInterpretation, setCorrectedInterpretation] = useState<string>(
    aiLearningExample?.corrected_interpretation || aiEnhancedInterpretation || ""
  );
  const [feedback, setFeedback] = useState<string>(
    aiLearningExample?.psychologist_feedback || ""
  );
  const [showEditInterpretation, setShowEditInterpretation] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // Check if we should show the AI feedback section
  const canProvideFeedback = IA_FEEDBACK_ENABLED && aiLearningExample && !aiLearningExample.reviewed;
  const hasProvidedRating = aiLearningExample?.reviewed;

  console.log("aiLearningExample", IA_FEEDBACK_ENABLED);

  const handleSubmitFeedback = async () => {
    if (!aiLearningExample) return;
    
    try {
      setSubmittingFeedback(true);
      setFeedbackError(null);
      
      // Create payload
      const payload: AiFeedbackPayload = {
        detail_id: parseInt(detailId),
        interpretation_id: aiLearningExample.id,
        rating: rating,
        corrected_interpretation: showEditInterpretation ? correctedInterpretation : undefined,
        feedback: showFeedbackForm ? feedback : undefined,
      };
      
      // Submit feedback through parent component handler
      await onSubmitFeedback(payload);
      setFeedbackSuccess(true);
      
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      setFeedbackError(error.message || "Error al enviar el feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Star rating component
  const StarRating = () => {
    return (
      <div className="flex flex-col space-y-2">
        <div className="flex items-center">
          <p className="mr-2 font-medium text-gray-700">Califique la calidad de la interpretación:</p>
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="p-1 focus:outline-none"
              >
                {star <= rating ? (
                  <MdStar className="h-8 w-8 text-yellow-500" />
                ) : (
                  <MdStarBorder className="h-8 w-8 text-gray-400 hover:text-yellow-300" />
                )}
              </button>
            ))}
          </div>
        </div>
        {rating > 0 && (
          <p className="text-sm text-gray-500">
            {rating === 1 && "Mala - Interpretación con errores graves"}
            {rating === 2 && "Regular - Interpretación con algunos errores"}
            {rating === 3 && "Buena - Interpretación aceptable"}
            {rating === 4 && "Muy buena - Interpretación con detalles precisos"}
            {rating === 5 && "Excelente - Interpretación clínicamente precisa"}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <Card extra="w-full p-4 md:p-6">
        <div>
          <div className="mb-2 flex items-center">
            <h3 className="font-semibold text-navy-700 dark:text-white">Interpretación IA</h3>
            {canProvideFeedback && (
              <button
                onClick={() => setShowEditInterpretation(!showEditInterpretation)}
                className="ml-auto rounded-lg bg-blue-500 px-3 py-1 text-sm text-white hover:bg-blue-600"
              >
                {showEditInterpretation ? "Cancelar" : "Corregir interpretación"}
              </button>
            )}
          </div>
          
          {/* Show either the corrected interpretation form or the read-only version */}
          {canProvideFeedback && showEditInterpretation ? (
            <div className="mb-4">
              <textarea
                value={correctedInterpretation}
                onChange={(e) => setCorrectedInterpretation(e.target.value)}
                rows={15}
                className="w-full rounded-lg border border-gray-300 p-3 text-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="Edite la interpretación generada por la IA..."
              />
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
              {(aiLearningExample?.corrected_interpretation || aiEnhancedInterpretation).split("\n\n").map((paragraph: string, index: number) => (
                <p key={index} className="mb-4 text-gray-700 dark:text-gray-400">
                  {paragraph}
                </p>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Feedback section */}
      {canProvideFeedback && (
        <div className="mt-6">
          <Card extra="w-full p-4 md:p-6">
            <div className="mb-4 flex items-center border-b border-gray-200 pb-2">
              <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                Comentarios sobre la Interpretación
              </h2>
              <div className="group relative ml-2">
                <MdQuestionMark className="h-5 w-5 cursor-help text-gray-500" />
                <div className="absolute left-0 top-6 z-10 hidden w-72 rounded-lg bg-white p-3 shadow-lg group-hover:block">
                  <p className="text-sm text-gray-700">
                    Proporcione comentarios sobre la interpretación de la IA para ayudar a mejorarla:
                  </p>
                  <ul className="mt-2 list-inside list-disc text-xs text-gray-600">
                    <li>Corregir errores técnicos o conceptuales</li>
                    <li>Reformular interpretaciones para hacerlas más precisas clínicamente</li>
                    <li>Añadir elementos faltantes que son importantes desde el punto de vista psicológico</li>
                    <li>Eliminar conclusiones incorrectas o especulativas</li>
                    <li>Mejorar la estructura y claridad de la interpretación</li>
                  </ul>
                </div>
              </div>
              <button
                onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                className="ml-auto rounded-lg bg-blue-500 px-3 py-1 text-sm text-white hover:bg-blue-600"
              >
                {showFeedbackForm ? "Cancelar" : "Añadir comentarios"}
              </button>
            </div>
            
            {/* Star Rating */}
            <StarRating />
            
            {/* Feedback text area */}
            {showFeedbackForm && (
              <div className="mt-4">
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={5}
                  className="w-full rounded-lg border border-gray-300 p-3 text-gray-700 focus:border-blue-500 focus:outline-none"
                  placeholder="Ingrese sus comentarios sobre la interpretación de la IA..."
                />
              </div>
            )}
            
            {/* Submit button */}
            {(rating > 0 || showEditInterpretation || showFeedbackForm) && (
              <div className="mt-4 flex items-center justify-end">
                {feedbackError && (
                  <p className="mr-auto text-sm text-red-500">{feedbackError}</p>
                )}
                
                {feedbackSuccess && (
                  <p className="mr-auto text-sm text-green-500">¡Feedback enviado con éxito!</p>
                )}
                
                <button
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || rating === 0}
                  className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-white hover:bg-green-600 disabled:bg-gray-300"
                >
                  {submittingFeedback ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <MdSave className="h-5 w-5" />
                      <span>Enviar Feedback</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Display psychologist feedback if available */}
      {aiLearningExample?.psychologist_feedback && hasProvidedRating && (
        <div className="mt-6">
          <Card extra="w-full p-4 md:p-6">
            <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-bold text-navy-700 dark:text-white">
              Feedback del Psicólogo
            </h2>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
              <p className="text-gray-700 dark:text-gray-400">{aiLearningExample.psychologist_feedback}</p>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export default AiInterpretation; 